import yup from "../../../../../utils/yup-extended";
import {yupResolver} from "@hookform/resolvers/yup";
import {Button, Input, message, Rate, Space} from "antd";
import {useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DollarOutlined,
  EditOutlined,
  HourglassOutlined,
  NumberOutlined,
  PhoneOutlined,
  StarFilled,
  VideoCameraOutlined,
} from "@ant-design/icons";
import {GetCustomerUpcomingCallsApiResponseSession} from "../../../../../react-query-hooks/session/upcoming-calls/types";
import {StyledUpcomingCallCard} from "../../../upcoming-calls/call-card/new/styles";
import {
  CoachUpcomingCallViewDetailsButton,
  UpcomingCallCardMetaInfo,
  UpcomingCallCardMetaInfoProps,
} from "../../../upcoming-calls/call-card/new";
import {StyledSessionDetailsDrawer} from "../../../service/sales-and-insights/customers-section/style";
import {FormField, FormFieldError} from "../../../../../design-system/form-field";
import {
  useCreateReviewApi,
  useUpdateReviewApi,
} from "../../../../../react-query-hooks/reviews/create-review";
import {
  CreateReviewPayload,
  CustomerGetReviewApiResponse,
} from "../../../../../react-query-hooks/reviews/types";
import {useGetCustomerReview} from "../../../../../react-query-hooks/reviews/get-reviews";
import {
  StyledCustomerReviewBlock,
  StyledCustomerReviewBlockStarsAndDate,
  StyledCustomerReviewBlockStarsAndDateWrapper,
} from "./styles";
import {FullPageLoader} from "../../../../full-page-loader";
import {handleDisplayName} from "../../../../../utils/profile";

const {TextArea} = Input;

const STAR_RATINGS = [
  {
    rate: 1,
    desc: "Very poor",
  },
  {
    rate: 2,
    desc: "Poor",
  },
  {
    rate: 3,
    desc: "Average",
  },
  {
    rate: 4,
    desc: "Good",
  },
  {
    rate: 5,
    desc: "Excellent",
  },
];

type CustomerPastCallCardProps = {
  session: GetCustomerUpcomingCallsApiResponseSession;
};

type PastCallCardViewDetailsDrawerProps = {
  title: string;
  onClose: () => void;
  isVisible: boolean;
  callMetaInfo: UpcomingCallCardMetaInfoProps["data"];
  description: string;
  showReviewFormDrawer: () => void;
  offeringId: ReviewFormProps["offeringId"];
  offeringType: ReviewFormProps["offeringType"];
  emailAddress: string;
};
type ReviewFormDrawerProps = {
  title: string;
  onClose: () => void;
  isVisible: boolean;
  offeringId: ReviewFormProps["offeringId"];
  offeringType: ReviewFormProps["offeringType"];
  emailAddress: string;
  customerFirstName: string;
  customerLastName: string;
  closeReviewFormDrawer: () => void;
};

export type CreateReviewFormValues = {
  // first_name: string;
  // last_name: string;
  // email_address: string;
  review: string;
  rating: number;
};

export const createReviewFormSchema = yup.object().shape({
  // first_name: yup.string().required("First name is required").trim(),
  // last_name: yup.string().required("Last name is required").trim(),
  // email_address: yup.string().required("Email address is required").trim(),
  review: yup.string().required("Review is required").trim(),
  rating: yup
    .number()
    .min(1, "Minimum rating is 1 star")
    .max(5, "Maximum rating is 5 stars")
    .typeError("Minimum rating is 1 star and Maximum rating is 5 stars")
    .required("Rating is required"),
});

export type ReviewFormProps = {
  offeringId: CreateReviewPayload["offering_id"];
  offeringType: CreateReviewPayload["offering_type"];
  reviewData?: CustomerGetReviewApiResponse;
  closeReviewFormDrawer: () => void;
  // canEdit?: boolean;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  /** Wider gutters between star columns (customer subscription review). */
  relaxedRatingStarSpacing?: boolean;
};

export function CustomerPastCallCard({
  session: {
    amount,
    booking_date,
    booking_month,
    booking_year,
    booking_type,
    bundle_session,
    coach_from_time,
    coach_first_name,
    coach_last_name,
    first_name,
    last_name,
    currency,
    is_bundle,
    phone_number,
    timeslot,
    email_address,
    // id,
    service: {callDuration, description, no_of_sessions, name},
    serviceId,
    coach_display_name,
  },
}: CustomerPastCallCardProps) {
  console.log(coach_display_name);
  const [isViewDetailsDrawerOpen, setIsViewDetailsDrawerOpen] = useState<boolean>(false);
  const [isReviewFormDrawerOpen, setIsReviewFormDrawerOpen] = useState<boolean>(false);
  const callAmount = amount === 0 ? "Free" : `${currency} ${amount.toLocaleString()}`;
  const callDate = new Date(
    Number(booking_year),
    Number(booking_month) - 1,
    Number(booking_date)
  ).toDateString();
  const callTime = coach_from_time || timeslot;
  const customerEmail = email_address;
  const callDescription = description.replace(/<[^>]+>/g, "");
  const offeringId = serviceId;
  const offeringType = is_bundle ? "bundle" : "booking";
  const callDurationText = `${callDuration} minutes`;
  const callLocationIcon =
    booking_type === "video" ? (
      <VideoCameraOutlined style={{color: "#222222"}} />
    ) : (
      <PhoneOutlined style={{color: "#222222"}} />
    );
  const bundleSessions = is_bundle
    ? `| ${bundle_session} of ${no_of_sessions} sessions`
    : "";
  const callLocationText: string =
    booking_type === "video"
      ? `Video call ${bundleSessions}`
      : `Phone call ${bundleSessions}`;

  const coachName = handleDisplayName({
    firstName: coach_first_name,
    lastName: coach_last_name,
    displayName: coach_display_name!,
  });
  const cardTitle = `${name} with ${coachName}`;
  const customerFirstName = first_name;
  const customerLastName = last_name;
  function generateCallCardMetaInfo() {
    const baseMetaInfo = [
      {
        icon: <DollarOutlined style={{color: "#222222"}} />,
        value: callAmount,
      },
      {
        icon: <CalendarOutlined style={{color: "#222222"}} />,
        value: callDate,
      },
      {
        icon: <ClockCircleOutlined style={{color: "#222222"}} />,
        value: callTime,
      },
      {
        icon: <HourglassOutlined style={{color: "#222222"}} />,
        value: callDurationText,
      },
      {
        icon: callLocationIcon,
        value: callLocationText,
      },
    ];

    if (booking_type === "audio") {
      return baseMetaInfo.concat([
        {
          icon: <NumberOutlined style={{color: "#222222"}} />,
          value: `${phone_number}`,
        },
      ]);
    } else {
      return baseMetaInfo;
    }
  }
  const callCardMetaInfo = generateCallCardMetaInfo();

  const showViewDetailsDrawer = () => {
    setIsViewDetailsDrawerOpen(true);
  };
  const closeViewDetailsDrawer = () => {
    setIsViewDetailsDrawerOpen(false);
  };
  const showReviewFormDrawer = () => {
    setIsReviewFormDrawerOpen(true);
  };
  const closeReviewFormDrawer = () => {
    setIsReviewFormDrawerOpen(false);
  };
  return (
    <StyledUpcomingCallCard title={cardTitle}>
      <Space direction="vertical" size={16} style={{display: "flex"}}>
        <UpcomingCallCardMetaInfo data={callCardMetaInfo} />
        <CoachUpcomingCallViewDetailsButton
          showViewDetailsDrawer={showViewDetailsDrawer}
        />
      </Space>
      {isViewDetailsDrawerOpen && (
        <PastCallCardViewDetailsDrawer
          isVisible={isViewDetailsDrawerOpen}
          onClose={closeViewDetailsDrawer}
          title={cardTitle}
          callMetaInfo={callCardMetaInfo}
          description={callDescription}
          showReviewFormDrawer={showReviewFormDrawer}
          offeringId={offeringId}
          offeringType={offeringType}
          emailAddress={customerEmail}
        />
      )}
      {isReviewFormDrawerOpen && (
        <ReviewFormDrawer
          isVisible={isReviewFormDrawerOpen}
          onClose={closeReviewFormDrawer}
          title={cardTitle}
          offeringId={offeringId}
          offeringType={offeringType}
          emailAddress={customerEmail}
          closeReviewFormDrawer={closeReviewFormDrawer}
          customerFirstName={customerFirstName}
          customerLastName={customerLastName}
        />
      )}
    </StyledUpcomingCallCard>
  );
}

export function PastCallCardViewDetailsDrawer({
  title,
  isVisible,
  onClose,
  callMetaInfo,
  description,
  showReviewFormDrawer,
  emailAddress,
  offeringId,
  offeringType,
}: PastCallCardViewDetailsDrawerProps) {
  const getCustomerReview = useGetCustomerReview({
    emailAddress: emailAddress,
    offering_id: offeringId,
    offering_type: offeringType,
  });
  // const getCustomerCanEditReview = useGetCustomerCanEditReview({
  //   emailAddress: emailAddress,
  //   offering_id: offeringId,
  //   offering_type: offeringType,
  // });
  return (
    <StyledSessionDetailsDrawer
      title={title}
      placement="right"
      onClose={onClose}
      visible={isVisible}
      closable={false}
      extra={
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
        ></Button>
      }
      footer={
        getCustomerReview.isSuccess ? null : (
          <>
            <Button size="small" type="primary" onClick={showReviewFormDrawer}>
              Leave a review
            </Button>
          </>
        )
      }
    >
      <div>
        <Space direction="vertical" style={{display: "flex"}} size={24}>
          <UpcomingCallCardMetaInfo data={callMetaInfo} />
          <p>{description}</p>
          <div>
            <Space direction="vertical" style={{display: "flex"}} size={16}>
              {getCustomerReview.isLoading ? (
                <FullPageLoader />
              ) : getCustomerReview.isError ? null : (
                // <Alert
                //   showIcon
                //   message="Error"
                //   description={getCustomerReview.error.message}
                //   type="error"
                // />
                <>
                  <p
                    style={{
                      fontWeight: 600,
                      fontSize: "1.6rem",
                    }}
                  >
                    Your review
                  </p>
                  <StyledCustomerReviewBlock>
                    <Space direction="vertical" style={{display: "flex"}} size={16}>
                      <StyledCustomerReviewBlockStarsAndDateWrapper>
                        <StyledCustomerReviewBlockStarsAndDate>
                          <Rate
                            disabled
                            defaultValue={getCustomerReview.data.rating}
                            value={getCustomerReview.data.rating}
                            style={{color: "#99C78F"}}
                            allowHalf
                          />
                          <p>
                            {new Date(getCustomerReview.data.updatedAt).toDateString()}
                          </p>
                        </StyledCustomerReviewBlockStarsAndDate>
                        {/* {getCustomerCanEditReview.data?.canEdit && ( */}
                        <Button
                          onClick={showReviewFormDrawer}
                          size="small"
                          type="text"
                          icon={<EditOutlined style={{fontSize: "1.6rem"}} />}
                        />
                        {/* )} */}
                      </StyledCustomerReviewBlockStarsAndDateWrapper>
                      <p>{getCustomerReview.data.review}</p>
                    </Space>
                  </StyledCustomerReviewBlock>
                </>
              )}
            </Space>
          </div>
        </Space>
      </div>
    </StyledSessionDetailsDrawer>
  );
}
export function ReviewFormDrawer({
  title,
  isVisible,
  onClose,
  offeringId,
  offeringType,
  emailAddress,
  closeReviewFormDrawer,
  customerFirstName,
  customerLastName,
}: ReviewFormDrawerProps) {
  const getCustomerReview = useGetCustomerReview({
    emailAddress: emailAddress,
    offering_id: offeringId,
    offering_type: offeringType,
  });
  // const getCustomerCanEditReview = useGetCustomerCanEditReview({
  //   emailAddress: emailAddress,
  //   offering_id: offeringId,
  //   offering_type: offeringType,
  // });
  return (
    <StyledSessionDetailsDrawer
      title={title}
      placement="right"
      onClose={onClose}
      visible={isVisible}
      closable={false}
      extra={
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
        ></Button>
      }
      footer={null}
    >
      <div>
        {/* {getCustomerReview.isLoading ? (
          <FullPageLoader />
        ) : ( */}
        <Space direction="vertical" style={{display: "flex"}} size={24}>
          <ReviewForm
            reviewData={getCustomerReview.data}
            offeringId={offeringId}
            offeringType={offeringType}
            closeReviewFormDrawer={closeReviewFormDrawer}
            // canEdit={getCustomerCanEditReview.data?.canEdit}
            customerFirstName={customerFirstName}
            customerLastName={customerLastName}
            customerEmail={emailAddress}
          />
        </Space>
        {/* )} */}
      </div>
    </StyledSessionDetailsDrawer>
  );
}

export function ReviewForm({
  offeringId,
  offeringType,
  reviewData,
  closeReviewFormDrawer,
  // canEdit,
  customerFirstName,
  customerLastName,
  customerEmail,
  relaxedRatingStarSpacing = false,
}: ReviewFormProps) {
  console.log({reviewData, offeringId});
  const [selectedHoverStarCount, setSelectedHoverStarCount] = useState(0);
  const createReviewMutation = useCreateReviewApi();
  const updateReviewMutation = useUpdateReviewApi();
  const {
    handleSubmit,
    formState: {errors},
    watch,
    control,
    reset,
  } = useForm<CreateReviewFormValues>({
    resolver: yupResolver(createReviewFormSchema),
    mode: "onChange",
    defaultValues: {
      // email_address: reviewData?.email_address,
      // first_name: customerFirstName,
      // last_name: customerLastName,
      review: reviewData?.review,
      rating: reviewData ? reviewData.rating : 0,
    },
  });

  async function onSubmit(values: CreateReviewFormValues) {
    // console.log({reviewValues: values});
    const payload: CreateReviewPayload = {
      email_address: customerEmail,
      first_name: customerFirstName,
      last_name: customerLastName,
      offering_id: offeringId,
      offering_type: offeringType,
      rating: values.rating,
      review: values.review,
    };

    try {
      if (reviewData) {
        await updateReviewMutation.mutateAsync({
          email_address: customerEmail,
          offering_id: offeringId,
          offering_type: offeringType,
          rating: values.rating,
          ratingId: reviewData.id,
          review: values.review,
        });
        message.success("Review updated successfully");
      } else {
        await createReviewMutation.mutateAsync(payload);
        message.success("Review created successfully");
      }

      reset();
      closeReviewFormDrawer();
    } catch {
      // Errors are surfaced by the mutation hooks.
    }
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Space direction="vertical" size={24} style={{display: "flex"}}>
        <p
          style={{
            fontWeight: 600,
            fontSize: "1.6rem",
          }}
        >
          Leave a review
        </p>
        {/* <FormField
          id={"review-first-name"}
          label={"First name"}
          renderInput={(id) => (
            <Controller
              control={control}
              name="first_name"
              render={({field: {onChange, value, ref, onBlur}, fieldState: {error}}) => (
                <Space direction="vertical" size={8} style={{display: "flex"}}>
                  <Input
                    id={id}
                    status={error?.message ? "error" : ""}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    size="large"
                    placeholder="Enter first name"
                    ref={ref}
                  />
                  <FormFieldError errorMessage={error?.message} />
                </Space>
              )}
            />
          )}
        />
        <FormField
          id={"review-last-name"}
          label={"Last name"}
          renderInput={(id) => (
            <Controller
              control={control}
              name="last_name"
              render={({field: {onChange, value, ref, onBlur}, fieldState: {error}}) => (
                <Space direction="vertical" size={8} style={{display: "flex"}}>
                  <Input
                    id={id}
                    status={error?.message ? "error" : ""}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    size="large"
                    placeholder="Enter last name"
                    ref={ref}
                  />
                  <FormFieldError errorMessage={error?.message} />
                </Space>
              )}
            />
          )}
        /> */}
        {/* <FormField
          id={"review-email"}
          label={"Email address"}
          renderInput={(id) => (
            <Controller
              control={control}
              name="email_address"
              render={({field: {onChange, value, ref, onBlur}, fieldState: {error}}) => (
                <Space direction="vertical" size={8} style={{display: "flex"}}>
                  <Input
                    id={id}
                    status={error?.message ? "error" : ""}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    size="large"
                    placeholder="Enter email address"
                    ref={ref}
                  />
                  <FormFieldError errorMessage={error?.message} />
                </Space>
              )}
            />
          )}
        /> */}
        <FormField
          id={"review"}
          label={"Review"}
          renderInput={(id) => (
            <Controller
              control={control}
              name="review"
              render={({field: {onChange, value, ref, onBlur}, fieldState: {error}}) => (
                <Space direction="vertical" size={8} style={{display: "flex"}}>
                  <TextArea
                    rows={4}
                    placeholder="Please leave a comment"
                    onChange={onChange}
                    value={value}
                    ref={ref}
                    onBlur={onBlur}
                  />
                  <FormFieldError errorMessage={error?.message} />
                </Space>
              )}
            />
          )}
        />
        <div>
          <Space direction="vertical" size={24} style={{display: "flex"}}>
            <p>How would you rate this service?</p>
            <Space direction="vertical" size={16} style={{display: "flex"}}>
              <Controller
                control={control}
                name="rating"
                render={({
                  field: {onChange, value, ref, onBlur},
                  fieldState: {error},
                }) => (
                  <Space size={8} direction="vertical" style={{display: "flex"}}>
                    <Space
                      size={relaxedRatingStarSpacing ? 24 : 8}
                      direction="horizontal"
                      style={{display: "flex"}}
                    >
                      {STAR_RATINGS.map((star) => (
                        <div
                          key={star.rate}
                          onMouseOver={() => setSelectedHoverStarCount(star.rate)}
                          onMouseOut={() => setSelectedHoverStarCount(0)}
                          onClick={() => {
                            onChange(star.rate);
                          }}
                          style={{
                            cursor: "pointer",
                            minWidth: relaxedRatingStarSpacing ? 44 : undefined,
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              alignSelf: "start",
                            }}
                          >
                            <StarFilled
                              style={{
                                color:
                                  star.rate <= value ||
                                  star.rate <= selectedHoverStarCount
                                    ? "#99C78F"
                                    : "#F0F0F0",
                                fontSize: relaxedRatingStarSpacing ? "2.25rem" : "2rem",
                              }}
                            />
                          </div>
                          <p>{star.desc}</p>
                        </div>
                      ))}
                      ({`${value}/5`})
                    </Space>
                    <FormFieldError errorMessage={error?.message} />
                  </Space>
                )}
              />
            </Space>
          </Space>
        </div>
        <Button
          type="primary"
          htmlType={"submit"}
          size="small"
          loading={createReviewMutation.isLoading || updateReviewMutation.isLoading}
          disabled={Object.keys(errors).length > 0 || Object.values(watch()).length < 1}
        >
          Submit
        </Button>
      </Space>
    </form>
  );
}
